import { TaskStatus } from '@/services/enums';
/**
 * K线信号服务（信号扫描、回测、优化等）
 */

import { apiClient } from './apiClient';
import type { SignalScannerRequest, SignalScannerResponse } from './klineTypes';
import { BarInterval, ScannerTimeFrame } from '@/services/enums';

export class KlineSignalService {
  /**
   * 信号嗅探
   */
  async scanSignals(params: SignalScannerRequest): Promise<SignalScannerResponse['data']> {
    const response = await apiClient.post<SignalScannerResponse['data']>('/kline/signal-scanner', params);

    if (!response.success) {
      throw new Error(response.message || '信号嗅探失败');
    }

    return response.data;
  }

  /**
   * 启动回测任务
   */
  async startBacktest(params: {
    startTime: number; // 开始时间戳（秒）
    endTime: number; // 结束时间戳（秒）
    bar?: ScannerTimeFrame;
    signalType?: 'ABSORPTION';
    rsiPeriod?: number;
    lookbackWindow?: number;
    priceSlopeMin?: number;
    priceSlopeMax?: number;
    rsiSlopeMin?: number;
    rsiSlopeMax?: number;
    volumeSlopeMin?: number;
    volumeSlopeMax?: number;
    maxVolatility?: number;
    maxGapHours?: number;
    obvSlopeMin?: number; // OBV斜率最小值（可选）
    priceAboveEma?: boolean; // 是否要求价格在EMA之上（可选）
    rsiMin?: number; // RSI最小值（可选，默认0，表示不限制）
    rsiMax?: number; // RSI最大值（可选，默认100，表示不限制）
    bbWidthMax?: number; // 布林带宽度最大值（可选，默认0.15）
    volumeRatioMin?: number; // 成交量比率最小值（可选，默认1.5）
  }): Promise<{
    taskId: string;
    status: string;
    message: string;
  }> {
    const response = await apiClient.post<{
      taskId: string;
      status: string;
      message: string;
    }>('/kline/signal-backtest', params);

    if (!response.success) {
      throw new Error(response.message || '启动回测失败');
    }

    return response.data;
  }

  /**
   * 获取回测任务状态
   */
  async getBacktestStatus(taskId: string): Promise<{
    taskId: string;
    startTime: number;
    endTime: number;
    bar: string;
    signalType: string;
    rsiPeriod: number;
    lookbackWindow: number;
    priceSlopeMin: number;
    priceSlopeMax: number;
    rsiSlopeMin?: number;
    rsiSlopeMax?: number;
    volumeSlopeMin?: number;
    volumeSlopeMax?: number;
    maxVolatility: number;
    maxGapHours: number;
    status: TaskStatus;
    createdAt: string;
    startedAt?: string;
    finishedAt?: string;
    error?: string;
    result?: {
      totalSignals: number;
      profitableSignals: number;
      successRate: string;
      parameters: any;
      timeRange: {
        startTime: number;
        endTime: number;
      };
    };
  }> {
    const response = await apiClient.get<{
      taskId: string;
      startTime: number;
      endTime: number;
      bar: string;
      signalType: string;
      rsiPeriod: number;
      lookbackWindow: number;
      priceSlopeMin: number;
      priceSlopeMax: number;
      rsiSlopeMin?: number;
      rsiSlopeMax?: number;
      volumeSlopeMin?: number;
      volumeSlopeMax?: number;
      maxVolatility: number;
      maxGapHours: number;
      status: TaskStatus;
      createdAt: string;
      startedAt?: string;
      finishedAt?: string;
      error?: string;
      result?: {
        totalSignals: number;
        profitableSignals: number;
        successRate: string;
        parameters: any;
        timeRange: {
          startTime: number;
          endTime: number;
        };
      };
    }>(`/kline/signal-backtest/${taskId}`);

    if (!response.success) {
      throw new Error(response.message || '获取回测任务状态失败');
    }

    return response.data;
  }

  /**
   * 启动参数优化任务
   */
  async startOptimize(params: {
    startTime: number; // 开始时间戳（秒）
    endTime: number; // 结束时间戳（秒）
    bar?: ScannerTimeFrame;
    signalType?: 'ABSORPTION';
    initialParams: {
      rsiPeriod: number;
      lookbackWindow: number;
      priceSlopeMin: number;
      priceSlopeMax: number;
      rsiSlopeMin?: number;
      rsiSlopeMax?: number;
      volumeSlopeMin?: number;
      volumeSlopeMax?: number;
      maxVolatility: number;
      maxGapHours: number;
      obvSlopeMin?: number; // OBV斜率最小值（可选）
      priceAboveEma?: boolean; // 是否要求价格在EMA之上（可选）
      rsiMin?: number; // RSI最小值（可选，默认0，表示不限制）
      rsiMax?: number; // RSI最大值（可选，默认100，表示不限制）
      bbWidthMax?: number; // 布林带宽度最大值（可选，默认0.15）
      volumeRatioMin?: number; // 成交量比率最小值（可选，默认1.5）
    };
  }): Promise<{
    taskId: string;
    status: string;
    message: string;
  }> {
    const response = await apiClient.post<{
      taskId: string;
      status: string;
      message: string;
    }>('/kline/signal-optimize', params);

    if (!response.success) {
      throw new Error(response.message || '启动优化失败');
    }

    return response.data;
  }

  /**
   * 获取优化任务状态
   */
  async getOptimizeStatus(taskId: string): Promise<{
    taskId: string;
    startTime: number;
    endTime: number;
    bar: string;
    signalType: string;
    initialParams: any;
    status: TaskStatus;
    createdAt: string;
    startedAt?: string;
    finishedAt?: string;
    error?: string;
    result?: {
      bestParams: any;
      bestSuccessRate: string;
      iterations: number;
      optimizationHistory: Array<{
        iteration: number;
        params: any;
        successRate: string;
      }>;
    };
  }> {
    const response = await apiClient.get<{
      taskId: string;
      startTime: number;
      endTime: number;
      bar: string;
      signalType: string;
      initialParams: any;
      status: TaskStatus;
      createdAt: string;
      startedAt?: string;
      finishedAt?: string;
      error?: string;
      result?: {
        bestParams: any;
        bestSuccessRate: string;
        iterations: number;
        optimizationHistory: Array<{
          iteration: number;
          params: any;
          successRate: string;
        }>;
      };
    }>(`/kline/signal-optimize/${taskId}`);

    if (!response.success) {
      throw new Error(response.message || '获取优化任务状态失败');
    }

    return response.data;
  }

  /**
   * 计算币种各项斜率和波动率
   */
  async calculateMetrics(params: {
    symbol: string;
    startTime: number; // 开始时间戳（秒）
    endTime: number; // 结束时间戳（秒）
    bar?: BarInterval; // 时间粒度
  }): Promise<{
    symbol: string;
    bar: string;
    startTime: number;
    endTime: number;
    candleCount: number;
    metrics: {
      priceSlope: number;
      rsiSlope: number;
      volumeSlope: number;
      rsiValue: number;
      maxVolatility: number;
      minVolatility: number;
      avgVolatility: number;
      volatilities: number[];
      avgVolume: number;
    };
    parameters: {
      rsiPeriod: number;
      lookbackWindow: number;
    };
  }> {
    const response = await apiClient.post<{
      symbol: string;
      bar: string;
      startTime: number;
      endTime: number;
      candleCount: number;
      metrics: {
        priceSlope: number;
        rsiSlope: number;
        volumeSlope: number;
        rsiValue: number;
        maxVolatility: number;
        minVolatility: number;
        avgVolatility: number;
        volatilities: number[];
        avgVolume: number;
      };
      parameters: {
        rsiPeriod: number;
        lookbackWindow: number;
      };
    }>('/kline/calculate-metrics', params);

    if (!response.success) {
      throw new Error(response.message || '计算指标失败');
    }

    return response.data;
  }
}

export const klineSignalService = new KlineSignalService();
